/** Mode de qualification paliers + bypass premier achat (contrat admin snake_case / client camelCase). */

export const QUALIFICATION_MODE = {
    POINTS: "POINTS",
    PURCHASES: "PURCHASES",
};

export const PURCHASES_RULES_BANNER =
    "Le palier suit le nombre d’achats et le montant. Les règles earn_points n’influencent plus le palier tant que ce mode est actif.";

export const MODE_SWITCH_CONFIRM =
    "Tous les clients seront recalculés immédiatement avec le nouveau mode. Ce n’est pas un upgrade / downgrade.";

export const BYPASS_HELP =
    "Si le premier achat d’un client atteint au moins ce montant, il reçoit immédiatement le palier choisi, sans attendre les seuils du mode. Uniquement sur la toute première vente.";

export const MODE_HELP = {
    POINTS: "Les paliers suivent les points de statut. Configurez des règles earn_points sur les ventes.",
    PURCHASES: "Les paliers suivent le nombre d’achats et le montant total. Chaque vente est comptée automatiquement.",
};

const LAST_CHANGE_LABELS = {
    upgrade: "Montée de palier",
    downgrade: "Descente de palier",
    no_change: "Renouvellement",
    admin_override: "Modification admin",
    mode_changed: "Changement de mode de qualification",
    first_purchase_bypass: "Bypass premier achat",
};

const HISTORY_TYPE_TO_LAST_CHANGE = {
    QUALIFICATION_MODE_CHANGED: "mode_changed",
    FIRST_PURCHASE_BYPASS: "first_purchase_bypass",
    UPGRADE: "upgrade",
    DOWNGRADE: "downgrade",
    NO_CHANGE: "no_change",
    RENEW: "no_change",
    ADMIN_OVERRIDE: "admin_override",
};

export function normalizeQualificationMode(value) {
    return String(value || "").toUpperCase() === QUALIFICATION_MODE.PURCHASES
        ? QUALIFICATION_MODE.PURCHASES
        : QUALIFICATION_MODE.POINTS;
}

export function isPurchasesMode(value) {
    return normalizeQualificationMode(value) === QUALIFICATION_MODE.PURCHASES;
}

export function qualificationModeLabel(value) {
    return isPurchasesMode(value) ? "Achats" : "Points";
}

export function lastChangeLabel(value) {
    if (value == null || value === "") return null;
    const key = String(value).trim();
    if (LAST_CHANGE_LABELS[key]) return LAST_CHANGE_LABELS[key];
    const mapped = HISTORY_TYPE_TO_LAST_CHANGE[key.toUpperCase()];
    if (mapped && LAST_CHANGE_LABELS[mapped]) return LAST_CHANGE_LABELS[mapped];
    return key;
}

export function historyEventToLastChange(entry) {
    if (!entry || typeof entry !== "object") return null;
    const raw = entry.lastChange
        || entry.last_change
        || entry.change
        || entry.type
        || entry.event_type
        || entry.eventType
        || null;
    if (raw == null) return null;
    const key = String(raw).trim();
    if (LAST_CHANGE_LABELS[key]) return key;
    return HISTORY_TYPE_TO_LAST_CHANGE[key.toUpperCase()] || key;
}

export function isSpecialLastChange(value) {
    const key = historyEventToLastChange({ lastChange: value }) || String(value || "");
    return key === "mode_changed" || key === "first_purchase_bypass";
}

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

export function isBaseTier(tier) {
    if (!tier) return false;
    return toNumber(tier.min_status_points ?? tier.minStatusPoints) === 0
        && toNumber(tier.min_purchase_count ?? tier.minPurchaseCount) === 0
        && toNumber(tier.min_purchase_amount ?? tier.minPurchaseAmount) === 0;
}

export function validateBypassConfig({
    enabled,
    minAmount,
    tierKey,
} = {}) {
    if (!enabled) return { valid: true, error: null };
    const amount = Number(minAmount);
    if (!Number.isFinite(amount) || amount < 1) {
        return { valid: false, error: "Le montant minimum du bypass doit être ≥ 1." };
    }
    if (!String(tierKey || "").trim()) {
        return { valid: false, error: "Choisissez un palier actif pour le bypass premier achat." };
    }
    return { valid: true, error: null };
}

export function validateTierPurchaseThresholds(form, qualificationMode, { isBase = false } = {}) {
    if (!isPurchasesMode(qualificationMode) || isBase) return { valid: true, error: null };
    const count = toNumber(form.min_purchase_count);
    const amount = toNumber(form.min_purchase_amount);
    if (count < 1 || amount < 1) {
        return {
            valid: false,
            error: "En mode Achats, le nombre d’achats et le montant minimum sont tous les deux requis (hors palier de base).",
        };
    }
    return { valid: true, error: null };
}

export function bypassErrorFromApi(detail) {
    const text = String(detail || "");
    if (text.includes("first_purchase_bypass_min_amount must be >= 1")) {
        return "Le montant minimum du bypass doit être ≥ 1.";
    }
    if (text.includes("first_purchase_bypass_tier_key is required")) {
        return "Le palier cible du bypass est obligatoire lorsqu’il est activé.";
    }
    if (text.includes("first_purchase_bypass_tier_key must match")) {
        return "Le palier du bypass doit exister et être actif.";
    }
    return null;
}

export function formatMoney(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "–";
    return n.toLocaleString("fr-FR");
}

export function normalizeLoyaltyHistory(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.items)) return raw.items;
    if (Array.isArray(raw?.history)) return raw.history;
    if (Array.isArray(raw?.events)) return raw.events;
    return [];
}

export function normalizePurchaseMovements(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.items)) return raw.items;
    if (Array.isArray(raw?.movements)) return raw.movements;
    return [];
}
